import { MapPin, Phone, Clock, MessageCircle, ExternalLink } from "lucide-react";
import { BUSINESS, telLink, waLink } from "../lib/business";
import ContactForm from "./ContactForm";

export const MapSection = () => {
  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-20 lg:py-28"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="text-center max-w-3xl mx-auto mb-14 reveal">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-cyan-400 font-semibold mb-4">
            <span className="w-8 h-px bg-cyan-400" />
            تواصل معنا
            <span className="w-8 h-px bg-cyan-400" />
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">
            موقعنا في حي الخليج — الرياض
          </h2>
          <p className="mt-5 text-lg text-slate-400 leading-relaxed">
            اتصل مباشرة، راسلنا على واتساب، أو افتح الموقع على الخريطة.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-6 lg:gap-8">
          {/* Left: Map + info */}
          <div className="lg:col-span-7 space-y-6 reveal">
            <div className="relative rounded-2xl overflow-hidden border border-slate-800 bg-slate-900 shadow-2xl">
              <div className="aspect-[4/3] lg:aspect-[16/11]">
                <iframe
                  src={BUSINESS.mapsEmbed}
                  title="موقع مؤسسة ماجد صالح المخلفي - الرياض حي الخليج"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="w-full h-full border-0"
                  data-testid="map-iframe"
                  allowFullScreen
                />
              </div>
              <div className="absolute bottom-4 left-4 right-4 flex flex-wrap gap-2 justify-between items-center">
                <a
                  href={BUSINESS.mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="open-map-button"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-slate-950 text-sm font-semibold shadow-xl transition"
                >
                  <MapPin className="w-4 h-4" />
                  افتح الموقع على الخريطة
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
                <span className="px-3 py-1.5 rounded-full bg-slate-950/80 backdrop-blur border border-slate-800 text-[11px] text-slate-300 font-medium">
                  Plus Code: {BUSINESS.plusCode}
                </span>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <a
                href={telLink}
                data-testid="contact-phone-card"
                className="group rounded-2xl border border-slate-800 bg-slate-900/60 hover:border-cyan-500/40 p-5 flex items-center gap-4 transition-all"
              >
                <div className="w-12 h-12 rounded-xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center group-hover:bg-cyan-500/20">
                  <Phone className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <div className="text-xs text-slate-400">اتصل مباشرة</div>
                  <div className="text-lg font-bold text-white tabular-nums">
                    {BUSINESS.phone}
                  </div>
                </div>
              </a>

              <a
                href={waLink()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-card"
                className="group rounded-2xl border border-slate-800 bg-slate-900/60 hover:border-[#25D366]/40 p-5 flex items-center gap-4 transition-all"
              >
                <div className="w-12 h-12 rounded-xl bg-[#25D366]/10 border border-[#25D366]/30 flex items-center justify-center group-hover:bg-[#25D366]/20">
                  <MessageCircle className="w-5 h-5 text-[#25D366]" />
                </div>
                <div>
                  <div className="text-xs text-slate-400">واتساب</div>
                  <div className="text-lg font-bold text-white">
                    محادثة فورية
                  </div>
                </div>
              </a>

              <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5 flex items-center gap-4 sm:col-span-2">
                <div className="w-12 h-12 rounded-xl bg-slate-950 border border-slate-800 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-cyan-400" />
                </div>
                <div className="min-w-0">
                  <div className="text-xs text-slate-400">العنوان</div>
                  <div className="text-[15px] font-semibold text-white leading-snug">
                    {BUSINESS.address}
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5 flex items-center gap-4 sm:col-span-2">
                <div className="w-12 h-12 rounded-xl bg-slate-950 border border-slate-800 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <div className="text-xs text-slate-400">ساعات العمل</div>
                  <div className="text-[15px] font-semibold text-white">
                    {BUSINESS.openingHours}
                  </div>
                </div>
              </div>

              {/* Social row */}
              <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5 sm:col-span-2">
                <div className="text-xs text-slate-400 mb-3">تابعنا على</div>
                <div className="flex flex-wrap gap-3">
                  <a
                    href={BUSINESS.socials.tiktok}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-tiktok-link"
                    className="group inline-flex items-center gap-2.5 px-4 py-2.5 rounded-lg bg-slate-950 border border-slate-800 hover:border-fuchsia-500/50 text-slate-200 hover:text-white transition-all"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      className="w-4 h-4 text-fuchsia-400"
                    >
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.94a8.16 8.16 0 0 0 4.77 1.52V7a4.85 4.85 0 0 1-1.84-.31z" />
                    </svg>
                    <span className="text-sm font-semibold">تيك توك</span>
                    <span className="text-xs text-slate-500 tabular-nums">
                      {BUSINESS.socials.tiktokHandle}
                    </span>
                  </a>
                  <a
                    href={BUSINESS.socials.telegram}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-telegram-link"
                    className="group inline-flex items-center gap-2.5 px-4 py-2.5 rounded-lg bg-slate-950 border border-slate-800 hover:border-sky-500/50 text-slate-200 hover:text-white transition-all"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      className="w-4 h-4 text-sky-400"
                    >
                      <path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z" />
                    </svg>
                    <span className="text-sm font-semibold">تيليجرام</span>
                    <span className="text-xs text-slate-500">
                      {BUSINESS.socials.telegramHandle}
                    </span>
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Contact form */}
          <div className="lg:col-span-5 reveal">
            <ContactForm />
          </div>
        </div>
      </div>
    </section>
  );
};

export default MapSection;
